/**
 * createOrder — POST /api/orders/ to create a product order during (or
 * after) an in-store visit.
 *
 * The server (OrderCreateInputSerializer / OrderViewSet.create) REQUIRES a
 * numeric `callid` (the parent CallLog id) plus `storeid`, `associateid`,
 * `projectid`, and a `products` list of `{ productid, orderqty, ... }`.
 *
 * In the offline-first world the parent check-in may not have synced yet, so
 * the caller usually has only the visit's localId — not a server callid. This
 * op therefore accepts EITHER:
 *   - `callId`: a known numeric CallLog id (online / already-synced path), or
 *   - `visitLocalId`: the local visit UUID, from which `callid` is resolved
 *     via resolveParentCallLogId once the parent check-in syncs (gated by
 *     `dependsOn`). Until then sync() returns 'pending'.
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, get, put } from '../../utils/indexedDB';
import { emit } from '../../utils/eventLog';
import {
  BASE_URL,
  jsonHeaders,
  classifyResponse,
  networkErrorResult,
  resolveParentCallLogId,
} from '../httpHelpers';

export interface OrderProductInput {
  productid: number;
  orderqty: number;
  quantity_on_hand?: number;
  shelf_tag_up?: boolean;
  /** #2022 Phase 4 — client-only: this line was captured via an OOS shelf-tag
   * scan (recorded as quantity_on_hand=0). Used to emit a distinct
   * `oos_recorded` event; NOT sent to the server (it infers OOS from qty 0). */
  is_oos?: boolean;
  /** #2340 — client-only: product description captured at scan time, stored on
   * the order record so the checkout manager-review always shows the real name
   * even if the product cache has been cleared. The POST body whitelists server
   * fields, so this is never sent. */
  description?: string;
}

export interface CreateOrderInput {
  /** Local visit UUID — resolves `callid` after the parent check-in syncs. */
  visitLocalId?: string;
  /** Known server CallLog id (online path); takes precedence over visitLocalId. */
  callId?: number;
  storeId: number; // legacystoreid
  projectId: number;
  associateId: number;
  products: OrderProductInput[];
}

/** The engine hands reconcile() `{ calllogid: <serverId> }` (here, the orderid). */
export interface CreateOrderResponse {
  calllogid?: number;
}

interface OrdersSingleton {
  key: string;
  orders: Array<{
    localId: string;
    storeId: number;
    // #2340 — project this capture belongs to, so the checkout summary can show
    // orders/inventory/OOS under the right task. Optional for records written
    // before this field existed.
    projectId?: number;
    products: OrderProductInput[];
    serverId?: number;
    createdAt: string;
  }>;
}

function ordersKey(visitLocalId?: string): string {
  return visitLocalId ? `orders:${visitLocalId}` : 'orders:unattached';
}

export const createOrderOp: WriteOp<CreateOrderInput, CreateOrderInput, CreateOrderResponse> = {
  kind: 'createOrder',

  async applyLocal(input, localId) {
    const key = ordersKey(input.visitLocalId);
    const existing =
      (await get<OrdersSingleton>(STORES.VISIT_SINGLETONS, key)) ||
      ({ key, orders: [] } as OrdersSingleton);
    existing.orders.push({
      localId,
      storeId: input.storeId,
      projectId: input.projectId,
      products: input.products,
      createdAt: new Date().toISOString(),
    });
    await put(STORES.VISIT_SINGLETONS, existing);

    // Event stream (#1950 P2 / #2022): emit one event PER KIND present in this
    // submission so the sync chip shows Orders / Inventory / Out-of-stock as
    // their own categories — even though all three ride this one op. Best-effort:
    // never break the write.
    //   order      = any line with orderqty > 0
    //   inventory  = a counted line (quantity_on_hand set) that isn't an OOS scan
    //   oos        = a line captured via an OOS shelf-tag scan (is_oos)
    const hasOrder = input.products.some((p) => (p.orderqty ?? 0) > 0);
    const hasOos = input.products.some((p) => p.is_oos === true);
    const hasInventory = input.products.some((p) => !p.is_oos && p.quantity_on_hand != null);
    const baseEvent = {
      store_id: input.storeId,
      project_id: input.projectId,
      calllog_id: input.callId ?? null,
    };
    const emits: Array<Promise<unknown>> = [];
    if (hasOrder) {
      const n = input.products.filter((p) => (p.orderqty ?? 0) > 0).length;
      emits.push(emit({ event_type: 'order_created', ...baseEvent, payload: { local_id: localId, line_count: n } }));
    }
    if (hasInventory) {
      const n = input.products.filter((p) => !p.is_oos && p.quantity_on_hand != null).length;
      emits.push(emit({ event_type: 'inventory_recorded', ...baseEvent, payload: { local_id: localId, line_count: n } }));
    }
    if (hasOos) {
      const n = input.products.filter((p) => p.is_oos === true).length;
      emits.push(emit({ event_type: 'oos_recorded', ...baseEvent, payload: { local_id: localId, line_count: n } }));
    }
    // Fallback: a submission with no recognizable kind still records as an order
    // so it never goes silently uncounted (preserves pre-#2022 behavior).
    if (emits.length === 0) {
      emits.push(
        emit({ event_type: 'order_created', ...baseEvent, payload: { local_id: localId, line_count: input.products.length } }),
      );
    }
    try {
      await Promise.all(emits);
    } catch {
      /* event emit is best-effort */
    }
  },

  serialize(input, localId) {
    return {
      action: 'createOrder',
      endpoint: '/api/orders/',
      method: 'POST',
      payload: input,
      priority: 50,
      maxRetries: 10,
      // No dependency when we already hold a numeric callid; otherwise gate on
      // the parent visit so we don't fire before its calllogid exists.
      dependsOn: input.callId != null ? null : (input.visitLocalId ?? null),
      relatedTaskUUID: localId,
      idempotencyKey: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as CreateOrderInput;

    let callid: number | null = null;
    if (input.callId != null) {
      callid = input.callId;
    } else if (input.visitLocalId) {
      const resolved = await resolveParentCallLogId(input.visitLocalId);
      if (resolved === undefined) {
        return {
          status: 'pending',
          retriable: true,
          errorMessage: 'Parent visit has not synced yet',
        };
      }
      callid = resolved;
    }
    if (callid == null) {
      // Server requires callid; without a known id or a synced visit we can
      // never satisfy the contract — terminal so it surfaces in Sync Issues
      // rather than retrying forever.
      return {
        status: 'failed',
        retriable: false,
        errorMessage: 'Order requires a CallLog id (callId or a synced visit)',
      };
    }

    const body: Record<string, unknown> = {
      callid,
      storeid: input.storeId,
      associateid: input.associateId,
      projectid: input.projectId,
      products: input.products.map((p) => {
        const item: Record<string, unknown> = { productid: p.productid, orderqty: p.orderqty };
        if (p.quantity_on_hand !== undefined) item.quantity_on_hand = p.quantity_on_hand;
        if (p.shelf_tag_up !== undefined) item.shelf_tag_up = p.shelf_tag_up;
        return item;
      }),
    };

    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${row.endpoint}`, {
        method: row.method,
        headers: jsonHeaders(row.idempotencyKey),
        body: JSON.stringify(body),
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;

    const data = await response.json().catch(() => ({}));
    const orderid = data?.data?.orderid ?? data?.orderid;
    return {
      status: 'success',
      serverId: typeof orderid === 'number' ? orderid : undefined,
      retriable: false,
    };
  },

  async reconcile(row, response) {
    const orderid = response?.calllogid;
    if (typeof orderid !== 'number') return;
    const input = row.payload as CreateOrderInput;
    const localId = row.relatedTaskUUID;
    if (!localId) return;
    const key = ordersKey(input.visitLocalId);
    const existing = await get<OrdersSingleton>(STORES.VISIT_SINGLETONS, key);
    if (!existing) return;
    const match = existing.orders.find((o) => o.localId === localId);
    if (match) match.serverId = orderid;
    await put(STORES.VISIT_SINGLETONS, existing);
  },

  dependsOn(input) {
    return input.callId != null ? null : (input.visitLocalId ?? null);
  },
};

export type CreateOrderOp = typeof createOrderOp;
